import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import client from '../api/client';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

export default function ForgotPassword() {
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await client.post('/auth/forgot-password/send-code', { email });
      setSuccess(language === 'zh' ? '验证码已发送，请查收邮件' : 'Verification code sent, please check your email');
      setStep('verify');
      startCountdown();
    } catch (err: any) {
      setError(err.response?.data?.detail || (language === 'zh' ? '发送失败，请重试' : 'Failed to send, please try again'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError(language === 'zh' ? '密码长度至少为 6 位' : 'Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(language === 'zh' ? '两次输入的密码不一致' : 'Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await client.post('/auth/forgot-password/reset', null, {
        params: { email, code, new_password: newPassword }
      });
      setSuccess(language === 'zh' ? '密码重置成功！正在跳转到登录页...' : 'Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || (language === 'zh' ? '重置失败，请重试' : 'Reset failed, please try again'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await client.post('/auth/forgot-password/send-code', { email });
      setSuccess(language === 'zh' ? '验证码已重新发送' : 'Verification code resent');
      startCountdown();
    } catch (err: any) {
      setError(err.response?.data?.detail || (language === 'zh' ? '发送失败，请重试' : 'Failed to send, please try again'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <LanguageSwitcher />
        
        <div style={styles.header}>
          <h1 style={styles.brand}>{t.brand}</h1>
          <p style={styles.slogan}>{t.slogan}</p>
        </div>
        
        <h2 style={styles.title}>
          {language === 'zh' ? '忘记密码' : 'Forgot Password'}
        </h2>
        <p style={styles.subtitle}>
          {step === 'email' 
            ? (language === 'zh' ? '输入您的邮箱，我们将发送验证码' : 'Enter your email to receive verification code')
            : (language === 'zh' ? '输入验证码和新密码' : 'Enter verification code and new password')
          }
        </p>
        
        {error && <p style={styles.error}>{error}</p>}
        {success && <p style={styles.success}>{success}</p>}
        
        {step === 'email' ? (
          <form onSubmit={handleSendCode}>
            <input
              style={styles.input}
              type="email"
              placeholder={language === 'zh' ? '邮箱地址' : 'Email address'}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <button style={styles.button} type="submit" disabled={loading}>
              {loading 
                ? (language === 'zh' ? '发送中...' : 'Sending...') 
                : (language === 'zh' ? '发送验证码' : 'Send Code')
              }
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <input
              style={styles.input}
              type="text"
              placeholder={language === 'zh' ? '6位验证码' : '6-digit code'}
              value={code}
              onChange={e => setCode(e.target.value)}
              maxLength={6}
              required
            />
            <input
              style={styles.input}
              type="password"
              placeholder={language === 'zh' ? '新密码（至少6位）' : 'New password (min 6 chars)'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
            />
            <input
              style={styles.input}
              type="password"
              placeholder={language === 'zh' ? '确认新密码' : 'Confirm new password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
            <button style={styles.button} type="submit" disabled={loading}>
              {loading 
                ? (language === 'zh' ? '重置中...' : 'Resetting...') 
                : (language === 'zh' ? '重置密码' : 'Reset Password')
              }
            </button>
            <button 
              type="button"
              style={{ ...styles.resendBtn, opacity: countdown > 0 ? 0.5 : 1 }}
              onClick={handleResendCode}
              disabled={countdown > 0 || loading}
            >
              {countdown > 0 
                ? `${countdown}s` 
                : (language === 'zh' ? '重新发送验证码' : 'Resend Code')
              }
            </button>
          </form>
        )}
        
        <p style={styles.link}>
          {language === 'zh' ? '想起密码了？' : 'Remember your password?'}
          <Link to="/login" style={styles.linkText}>
            {language === 'zh' ? '返回登录' : 'Back to Login'}
          </Link>
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center',
    minHeight: '100vh', 
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  card: {
    background: '#fff', 
    padding: '48px 40px', 
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)', 
    width: '100%',
    maxWidth: '420px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '2px solid #f0f0f0',
  },
  brand: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#667eea',
    margin: '0 0 8px 0',
  },
  slogan: {
    fontSize: '14px',
    color: '#888',
    margin: 0,
  },
  title: { 
    textAlign: 'center', 
    marginBottom: '8px', 
    color: '#333',
    fontSize: '24px',
    fontWeight: '600',
  },
  subtitle: {
    textAlign: 'center',
    color: '#666',
    fontSize: '14px',
    marginBottom: '20px',
  },
  input: {
    width: '100%', 
    padding: '14px 16px', 
    marginBottom: '16px',
    border: '2px solid #e8e8e8', 
    borderRadius: '8px', 
    fontSize: '15px',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s',
    outline: 'none',
  },
  button: {
    width: '100%', 
    padding: '14px', 
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff', 
    border: 'none', 
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    marginBottom: '12px',
  },
  resendBtn: {
    width: '100%',
    padding: '12px',
    background: '#fff',
    color: '#667eea',
    border: '2px solid #667eea',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  error: { 
    color: '#ff4d4f', 
    textAlign: 'center', 
    marginBottom: '16px',
    padding: '12px',
    background: '#fff2f0',
    borderRadius: '8px',
    fontSize: '14px',
  },
  success: {
    color: '#52c41a',
    textAlign: 'center',
    marginBottom: '16px',
    padding: '12px',
    background: '#f6ffed',
    borderRadius: '8px',
    fontSize: '14px',
  },
  link: { 
    textAlign: 'center', 
    marginTop: '24px', 
    fontSize: '14px',
    color: '#666',
  },
  linkText: {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '600',
    marginLeft: '4px',
  },
};
